/* eslint-disable import/no-duplicates */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Icon,
  useDisclosure,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Divider,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { RiFileListLine, RiCheckLine, RiUserLine } from 'react-icons/ri';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { format, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import {
  formatReportDate,
  maxReportDate,
  MIN_REPORT_DATE,
} from '../../../../utils/formatReportDate';

import { ModalComponent } from '..';
import Input from '../../../Global/Input';
import { UserSelect } from '../../../Global/UserSelect';
import { apllyToast } from '../../../Global/Toast2.0';
import { isDirectorSector } from '../../../../utils/kmRateTable';
import {
  IExpenseReport,
  IExpenseItem,
  recalculateKmGroupItems,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { usersService, IUserOption } from '../../../../services/usersService';

interface IModalProps {
  expenseReports: IExpenseReport[];
  onConsolidate: (
    consolidatedReport: Omit<
      IExpenseReport,
      'id' | 'created_at' | 'updated_at'
    >,
    originalReportIds: string[],
    period: { startDate: string; endDate: string },
  ) => void;
}

export function ModalConsolidateExpenses({
  expenseReports,
  onConsolidate,
}: IModalProps) {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const formRef = React.useRef<FormHandles>(null);

  const [selectedDirector, setSelectedDirector] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [managerId, setManagerId] = useState('');
  const [users, setUsers] = useState<IUserOption[]>([]);

  // Carregar usuários ao abrir
  useEffect(() => {
    if (isOpen) {
      usersService
        .list()
        .then(data => setUsers(data))
        .catch(err => console.error('Erro ao carregar usuários:', err));
    }
  }, [isOpen]);

  // Filtrar relatórios baseado nos critérios
  const filteredReports = useMemo(() => {
    if (!selectedDirector || !startDate || !endDate) return [];

    // "T00:00:00" força o parse em horário LOCAL em vez de UTC — sem isso,
    // "2026-08-24" vira meia-noite UTC, que em horário do Brasil (UTC-3)
    // já é 23/08 às 21h, fazendo a data exibida "voltar" um dia.
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);

    // enquanto o ano e digitado o input entrega datas parciais (ano 0002, 0020,
    // 0202), que invertem o intervalo. o isWithinInterval abaixo lanca
    // RangeError quando o fim vem antes do inicio e derruba a tela inteira
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start > end
    ) {
      return [];
    }

    return expenseReports.filter(report => {
      // Ignorar consolidados
      if (report.isConsolidated || report.is_consolidated) return false;
      // Ignorar assinados (ou em processo de assinatura)
      const status = report.signatureStatus || report.signature_status;
      if (status === 'signed' || status === 'sent') return false;
      if (report.signature) return false;

      // data invalida vira Invalid Date e o isWithinInterval abaixo devolve
      // false, entao o relatorio fica de fora da consolidacao sem avisar
      const reportDate = new Date(`${report.date}T00:00:00`);
      const matchesDirector = report.director === selectedDirector;
      const matchesDate = isWithinInterval(reportDate, { start, end });
      return matchesDirector && matchesDate;
    });
  }, [expenseReports, selectedDirector, startDate, endDate]);

  // Setor do usuário selecionado — usado para achar a faixa de KM certa ao
  // somar todo o km consolidado (ver recalculateKmGroupItems).
  const selectedSectorId = users.find(
    u => u.id === selectedDirector,
  )?.sector_id;

  // Diretoria não precisa de gestor (backend libera sector_id = SPECIAL_SECTOR_ID)
  const isDirectorUser = isDirectorSector(selectedSectorId);

  // Gestor em comum entre os relatórios filtrados; vazio se forem diferentes
  const commonManagerId = useMemo(() => {
    const ids = new Set(
      filteredReports.map(r => r.manager_id || r.manager?.id || ''),
    );
    if (ids.size !== 1) return '';
    const [id] = Array.from(ids);
    return id !== selectedDirector ? id : '';
  }, [filteredReports, selectedDirector]);

  // Ao trocar o usuário ou quando o gestor em comum muda: pré-preenche com o
  // gestor em comum, ou limpa se os relatórios tiverem gestores diferentes
  // (obriga uma escolha consciente). Recarregar a lista não sobrescreve a
  // troca manual, pois nenhum dos dois valores muda.
  useEffect(() => {
    setManagerId(commonManagerId);
  }, [commonManagerId, selectedDirector]);

  // Calcular totais
  const consolidatedData = useMemo(() => {
    const rawItems: IExpenseItem[] = [];
    const reportIds: string[] = [];

    filteredReports.forEach(report => {
      reportIds.push(report.id);
      rawItems.push(...report.items);
    });

    // Itens de km com taxa fixa (diretoria/lista especial) mantêm seu valor
    // individual; os demais itens de "Quilometragem adicional" têm o valor
    // recalculado com base na SOMA de todo o km consolidado — não mais no km
    // de cada lançamento isolado.
    const allItems = recalculateKmGroupItems(rawItems, selectedSectorId);

    const totals = {
      refeicao: 0,
      km: 0,
      pedagio: 0,
      outros: 0,
      valorTotal: 0,
    };

    allItems.forEach(item => {
      if (item.expense_type === 'Refeição') {
        totals.refeicao += 1;
      } else if (item.expense_type === 'Quilometragem adicional') {
        totals.km += 1;
      } else if (item.expense_type === 'Pedágio adicional') {
        totals.pedagio += 1;
      } else if (item.expense_type === 'Outros') {
        totals.outros += 1;
      }
      totals.valorTotal += item.amount || 0;
    });

    return {
      allItems,
      reportIds,
      totals,
      totalItems: allItems.length,
      totalReports: filteredReports.length,
    };
  }, [filteredReports, selectedSectorId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Limpa todos os campos ao fechar, cancelar ou depois de consolidar
  const handleClose = () => {
    setSelectedDirector('');
    setStartDate('');
    setEndDate('');
    setManagerId('');
    onClose();
  };

  const handleConsolidate = () => {
    if (filteredReports.length === 0) return;

    if (!isDirectorUser && !managerId) {
      apllyToast('warning', 'Selecione o gestor responsável pela aprovação!');
      return;
    }

    // Pegar os dados do primeiro relatório como base
    const baseReport = filteredReports[0];

    // Coletar IDs dos relatórios originais
    const originalReportIds = filteredReports.map(report => report.id);

    // Criar relatório consolidado (sem assinatura - será assinado digitalmente depois)
    const consolidatedReport: Omit<
      IExpenseReport,
      'id' | 'created_at' | 'updated_at'
    > = {
      director: selectedDirector,
      manager_id: isDirectorUser ? undefined : managerId,
      date: endDate, // Data final do período
      sector: baseReport.sector,
      section: baseReport.section,
      cost_center: baseReport.cost_center,
      items: consolidatedData.allItems,
      signatureStatus: 'pending', // Aguardando envio para assinatura digital
    };

    onConsolidate(consolidatedReport, originalReportIds, {
      startDate,
      endDate,
    });
    handleClose();
  };

  const setThisWeek = () => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Segunda-feira
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 }); // Domingo

    setStartDate(format(weekStart, 'yyyy-MM-dd'));
    setEndDate(format(weekEnd, 'yyyy-MM-dd'));
  };

  return (
    <>
      <Button
        size="sm"
        colorScheme="purple"
        fontWeight="medium"
        onClick={onOpen}
        leftIcon={<Icon as={RiFileListLine} />}
      >
        <Text>Consolidar Relatórios</Text>
      </Button>

      <ModalComponent
        title="Consolidação de Relatórios - Fechamento Semanal"
        isOpen={isOpen}
        onClose={handleClose}
        sizeSpecifyc="3xl"
      >
        <ModalBody color="#1a202c">
          <VStack spacing={4} align="stretch">
            {/* Filtros */}
            <Box bg="purple.50" p={4} borderRadius="md">
              <Text fontWeight="bold" mb={3} color="purple.700">
                Selecione o Período e Usuário
              </Text>

              <Form
                ref={formRef}
                onSubmit={() => {
                  // Form não precisa de submit, apenas para estrutura
                }}
              >
                <Box mb={3}>
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    Usuário Responsável
                  </Text>
                  <UserSelect
                    users={users}
                    value={selectedDirector}
                    onChange={setSelectedDirector}
                    placeholder="Pesquisar e selecionar usuário"
                  />
                </Box>

                {selectedDirector && !isDirectorUser && (
                  <Box mb={3}>
                    <HStack spacing={2} mb={2}>
                      <Icon as={RiUserLine} fontSize="16" />
                      <Text fontSize="sm" fontWeight="600">
                        Gestor Responsável (aprovação)
                      </Text>
                    </HStack>
                    <UserSelect
                      users={users.filter(u => u.id !== selectedDirector)}
                      value={managerId}
                      onChange={setManagerId}
                      placeholder="Pesquisar e selecionar gestor"
                    />
                  </Box>
                )}

                <HStack spacing={3} align="end">
                  <Box flex={1}>
                    <p>Data Início</p>
                    <Input
                      type="date"
                      name="start_date"
                      min={MIN_REPORT_DATE}
                      max={maxReportDate()}
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <p>Data Fim</p>
                    <Input
                      type="date"
                      name="end_date"
                      min={startDate || MIN_REPORT_DATE}
                      max={maxReportDate()}
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                    />
                  </Box>
                  <Button
                    colorScheme="purple"
                    variant="outline"
                    size="md"
                    onClick={setThisWeek}
                  >
                    Esta Semana
                  </Button>
                </HStack>
              </Form>
            </Box>

            <Divider />

            {/* Resultado da Consolidação */}
            {filteredReports.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Nenhum relatório encontrado</AlertTitle>
                  <AlertDescription>
                    Selecione um usuário e período para visualizar os relatórios
                    que serão consolidados.
                  </AlertDescription>
                </Box>
              </Alert>
            ) : (
              <VStack spacing={4} align="stretch">
                {/* Resumo */}
                <Box bg="green.50" p={4} borderRadius="md">
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" color="green.700">
                      Resumo da Consolidação
                    </Text>
                    <Badge colorScheme="green" fontSize="md">
                      {filteredReports.length}{' '}
                      {filteredReports.length === 1
                        ? 'relatório'
                        : 'relatórios'}
                    </Badge>
                  </HStack>

                  <HStack spacing={6} wrap="wrap">
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Total de Itens
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.700">
                        {consolidatedData.totalItems}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Valor Total
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="green.700">
                        {formatCurrency(consolidatedData.totals.valorTotal)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="gray.600">
                        Período
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {formatReportDate(startDate, 'dd/MM')} a{' '}
                        {formatReportDate(endDate, 'dd/MM/yyyy')}
                      </Text>
                    </Box>
                  </HStack>
                </Box>

                {/* Breakdown por Tipo */}
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Distribuição por Tipo
                  </Text>
                  <HStack spacing={2} wrap="wrap">
                    {consolidatedData.totals.refeicao > 0 && (
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                        {consolidatedData.totals.refeicao} Refeições
                      </Badge>
                    )}
                    {consolidatedData.totals.km > 0 && (
                      <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                        {consolidatedData.totals.km} Quilometragens
                      </Badge>
                    )}
                    {consolidatedData.totals.pedagio > 0 && (
                      <Badge colorScheme="orange" fontSize="md" px={3} py={1}>
                        {consolidatedData.totals.pedagio} Pedágios
                      </Badge>
                    )}
                    {consolidatedData.totals.outros > 0 && (
                      <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                        {consolidatedData.totals.outros} Outros
                      </Badge>
                    )}
                  </HStack>
                </Box>

                {/* Lista de Relatórios a serem Consolidados */}
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Relatórios que serão consolidados:
                  </Text>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Data</Th>
                        <Th>Setor/Seção/CC</Th>
                        <Th>Itens</Th>
                        <Th isNumeric>Valor</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredReports.map(report => {
                        // Total do relatório original (antes da consolidação),
                        // usando o valor já calculado de cada item.
                        const total = report.items.reduce(
                          (sum, item) => sum + (item.amount || 0),
                          0,
                        );

                        return (
                          <Tr key={report.id}>
                            <Td>
                              {formatReportDate(report.date, 'dd/MM/yyyy')}
                            </Td>
                            <Td>
                              {report.sector} / {report.section} /{' '}
                              {report.cost_center}
                            </Td>
                            <Td>
                              <Badge colorScheme="gray">
                                {report.items.length}{' '}
                                {report.items.length === 1 ? 'item' : 'itens'}
                              </Badge>
                            </Td>
                            <Td isNumeric fontWeight="medium">
                              {formatCurrency(total)}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>

                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Atenção</AlertTitle>
                    <AlertDescription>
                      Um novo relatório consolidado será criado contendo TODOS
                      os {consolidatedData.totalItems} itens dos{' '}
                      {filteredReports.length} relatórios selecionados. Os
                      relatórios originais NÃO serão excluídos.
                    </AlertDescription>
                  </Box>
                </Alert>
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            leftIcon={<Icon as={RiCheckLine} />}
            onClick={handleConsolidate}
            isDisabled={filteredReports.length === 0}
          >
            Consolidar{' '}
            {filteredReports.length > 0 &&
              `(${filteredReports.length} ${
                filteredReports.length === 1 ? 'relatório' : 'relatórios'
              })`}
          </Button>
        </ModalFooter>
      </ModalComponent>
    </>
  );
}
